// Sugalarm — app-styled confirm / alert modal (cycle 210).
//
// Replaces the stock `window.confirm` / `window.alert` OS dialog so no
// native dialog ever appears over the app — on a locked kiosk the OS
// dialog is off-brand and (on Android) another tap-target outside the
// app's control. Same chrome as the cycle-42 ConfirmDeleteModal /
// cycle-34.20 EditTreatmentModal: fixed blurred backdrop, 220ms scale-
// in, tap-outside + Escape dismiss, token-only colors.
//
// Two shapes, driven by `cancelLabel`:
//   - confirm — primary action + Cancel (optional destructive styling).
//   - alert   — single dismiss button (pass `cancelLabel={null}`).
//
// Presentational + controlled: `confirmContext` owns the open/queue
// state and the promise wiring. Buttons are ≥44px touch targets.

import { useEffect, useState } from "react";

export interface ConfirmModalProps {
  open: boolean;
  /** Uppercase eyebrow. Defaults to "Confirm" / "Remove" by destructive. */
  title?: string;
  message: string;
  /** Primary button label (defaults to "OK"). */
  confirmLabel?: string;
  /** Cancel button label. Pass `null` for the single-button alert shape. */
  cancelLabel?: string | null;
  /** Red destructive primary + eyebrow (delete / remove / revoke). */
  destructive?: boolean;
  onConfirm: () => void;
  /** Cancel / tap-outside / Escape — also the alert acknowledge path. */
  onCancel: () => void;
}

export function ConfirmModal({
  open,
  title,
  message,
  confirmLabel = "OK",
  cancelLabel,
  destructive = false,
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  const [mounted, setMounted] = useState<boolean>(open);
  const isAlert = cancelLabel === null;
  const eyebrow = title ?? (destructive ? "Remove" : "Confirm");

  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    const t = window.setTimeout(() => setMounted(false), 220);
    return () => window.clearTimeout(t);
  }, [open]);

  // Escape dismisses (= cancel / acknowledge) on kiosk + web where a
  // physical keyboard exists.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!mounted) return null;

  const primaryBg = destructive ? "var(--zone-urgent)" : "var(--cta-fill)";
  const primaryInk = destructive ? "#fff" : "var(--cta-ink)";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={eyebrow}
      onClick={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 365,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: open ? "rgba(0, 0, 0, 0.32)" : "transparent",
        backdropFilter: "blur(3px)",
        transition: "background 220ms ease-out",
        pointerEvents: open ? "auto" : "none",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(420px, 100%)",
          padding: "22px 22px 18px",
          background: "var(--bg-elev)",
          border: "0.5px solid var(--line-strong)",
          borderRadius: 18,
          boxShadow: "0 16px 48px rgba(0, 0, 0, 0.4)",
          transform: open
            ? "scale(1) translateY(0)"
            : "scale(0.94) translateY(8px)",
          opacity: open ? 1 : 0,
          transition: "transform 220ms ease-out, opacity 200ms ease-out",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <div
          style={{
            fontSize: "var(--type-floor)",
            fontWeight: 700,
            letterSpacing: "0.10em",
            textTransform: "uppercase",
            color: destructive ? "var(--zone-urgent)" : "var(--ink-3)",
          }}
        >
          {eyebrow}
        </div>

        <div style={{ fontSize: 15, color: "var(--ink)", lineHeight: 1.45 }}>
          {message}
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 2 }}>
          {!isAlert ? (
            <button
              type="button"
              onClick={onCancel}
              style={{
                appearance: "none",
                flex: 1,
                minHeight: 44,
                border: "0.5px solid var(--line-strong)",
                background: "transparent",
                color: "var(--ink-2)",
                borderRadius: 10,
                padding: "10px 14px",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {cancelLabel ?? "Cancel"}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onConfirm}
            style={{
              appearance: "none",
              flex: 1,
              minHeight: 44,
              border: 0,
              background: primaryBg,
              color: primaryInk,
              borderRadius: 10,
              padding: "10px 14px",
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
